import React, { useContext, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import accounting from 'accounting';
import {
  Theme,
  Button,
  UnawardIcon,
  CalculatorIcon,
  AlertService,
  Timing,
  SparkleIcon,
  SparkleGradientIcon,
  icons,
  MiniTooltip,
  AlertTriangleIcon,
} from '@spoiler-alert/ui-library';
import { createUseStyles } from 'react-jss';
import { useMutation } from '@apollo/client';
import {
  calculateWeight,
  calculatePallets,
  calculateRecovery,
  calculateNetRevenue,
  calculateLaneCost,
  calculateTruckCount,
  applicableBuyerMinimums,
  invalidErrorCodes,
  TrucklaneActionInProgressContext,
} from './trucklane-utility';
import { offerlistingStatuses } from '../../enums';
import {
  AcceptShiftedOfferListings,
  AwardSuggestedOfferListings,
  UnawardOfferListingsByTruckLane,
  AwardReservePriceExceptions,
} from '../../graphql/mutations';
import { OfferComparisonStrings } from '../../string-resources';
import { ActiveOfferShiftsQuery, AwardSummaryQuery, trucklanePollingQuery, trucklaneExceptionsQuery } from '../../graphql/queries';
import { Store } from '../../store';
import Tracking from '../../utilities/tracking-apis';
import OfferShiftContext, { formatShiftsForAcceptance } from '../offer-comparison/offer-shift-context';
import { ACTIVE_STEP } from '../offer-comparison/active-step-enums';
import { checkFeature } from '../../helpers';
import { featureFlags } from '../../enums';

const styles = {
  suggestedSummary: {
    backgroundColor: Theme.green5,
  },
  summary: (selected) => ({
    backgroundColor: selected ? Theme.green5 : Theme.grey5,
  }),
  summaryTable: {
    backgroundColor: Theme.white,
    padding: 12,
    margin: 16,
    border: `1px solid ${Theme.borderColor}`,
    borderRadius: 2,
    width: 450,
    minHeight: 250,
    display: 'flex',
    flexDirection: 'column',
    '& h1': {
      fontSize: '14px',
      fontWeight: '500',
      color: Theme.grey,
    },
  },
  summaryTableRow: {
    display: 'flex',
    padding: '4px',
    borderTop: `1px solid ${Theme.grey10}`,
    '&:first-child': {
      borderTop: 'none',
    },
  },
  summaryTableCell: {
    flex: '1 1 0',
    minWidth: 0,
  },
  summaryTableHeader: {
    flex: '1 1 0',
    minWidth: 0,
    color: Theme.grey,
    fontSize: '12px',
    fontWeight: 'normal',
  },
  summaryTableHeaderIq: {
    extend: 'summaryTableHeader',
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    '& svg': {
      width: 12,
      height: 12,
    },
  },
  summaryTableInfo: {
    flex: '1 1 0',
    fontSize: '12px',
    fontWeight: '500',
  },
  sumaryTableEmptyInfo: {
    extend: 'summaryTableInfo',
    color: Theme.grey50,
  },
  summaryActions: {
    display: 'flex',
    marginTop: 'auto',
    alignItems: 'center',
    '& button': {
      width: '100% !important',
      '&:not(:first-child)': {
        marginLeft: 8,
      },
    },
  },
  recalculationBanner: {
    backgroundColor: Theme.orange5,
    display: 'flex',
    justifyContent: 'center',
    marginTop: 4,
    marginBottom: 8,
    padding: '4px 0px',
    '& svg': {
      fill: Theme.orangeDark,
      height: 16,
      width: 16,
      margin: '2px',
    },
    '& h1': {
      fontSize: '14px',
      fontWeight: '500',
      color: Theme.orangeDark,
      margin: 0,
    },
  },
  iq_icon: {
    '& svg': {
      maxWidth: 20,
      maxHeight: 20,
      width: 20,
      height: 20,
      marginTop: 2,
    },
  },
  // reserve price exception enabled styles
  rightContainer: {
    flex: '1 1 435px',
    minWidth: 435,
    backgroundColor: Theme.grey5,
    padding: 24,
    display: 'flex',
    flexDirection: 'column',
    borderRadius: '0 6px 6px 0',
  },
  summaryTableOverride: {
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
  },
  summaryActionsOverride: {
    display: 'flex',
    marginTop: 'auto',
    alignItems: 'center',
    gap: 12,
    '& button:last-child': {
      flex: 1,
    },
  },
  alignRight: {
    textAlign: 'right',
  },
  cardIqSummary: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    marginBottom: 16,
  },
  cardIqTitle: {
    color: Theme.primaryColor,
    fontSize: 14,
    fontWeight: '400',
    display: 'flex',
    alignItems: 'center',
    gap: 4,
  },
  cardIqDescription: {
    fontSize: 16,
    fontWeight: '400',
  },
  cardIqHighlight: {
    fontWeight: '500',
  },
  minimumsAlert: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    color: Theme.orangeDark,
    fontSize: 14,
    fontWeight: '500',
    marginTop: 12,
    '& svg': {
      width: 16,
      height: 16,
      stroke: Theme.orangeDark,
    },
  },
  minimumsIcon: {
    width: 14,
    height: 14,
    marginLeft: 4,
    verticalAlign: 'text-bottom',
    stroke: Theme.orangeDark,
  },
};
const useStyles = createUseStyles(styles);

const TrucklaneCardSummary = ({
  offers,
  recalculation,
  trucklaneId,
  activeStep,
  selected,
  deSelect,
  actionLoading,
  childAction,
  user,
  ignoreOffers,
  ignoreOffersLoading,
  restoreOffers,
  restoreOffersLoading,
  buyerMinimums,
}) => {
  const classes = useStyles(selected);
  const tableData = ['WEIGHT', 'PALLETS', 'NET REVENUE', 'RECOVERY'];
  const truckMaximumsSet = user?.site?.truckMaximums?.mode !== 'none' && (user?.site?.truckMaximums?.weight || user?.site?.truckMaximums?.pallets);
  // Both sit under PALLETS with the other load metrics, and both only apply when a maximum is configured —
  // with mode none there is no truck count and no multiplier to make a lane cost meaningful.
  if (truckMaximumsSet) tableData.splice(tableData.indexOf('PALLETS') + 1, 0, 'TRUCK COUNT', 'LANE COST');
  const [tableHeaders, setTableHeaders] = useState([]);
  const { setActionInProgress } = useContext(TrucklaneActionInProgressContext);
  const { offerShiftsMappedToTrucklanes = new Map() } = useContext(OfferShiftContext) || {};
  const awardedOffers = offers.some((offer) => offerlistingStatuses.AWARDED === offer.status);
  const [unawardOffersMutation, { loading: unawardOffersLoading }] = useMutation(UnawardOfferListingsByTruckLane);
  const [awardOffersMutation, { loading: awardOffersLoading }] = useMutation(AwardSuggestedOfferListings);
  const [awardOpportunitiesMutation, { loading: awardOpportunitiesLoading }] = useMutation(AwardReservePriceExceptions);
  const [acceptOfferShifts, { loading: acceptingOfferShiftLoading }] = useMutation(AcceptShiftedOfferListings);

  const loadingAwards = acceptingOfferShiftLoading || awardOffersLoading;
  const reservePriceExceptionsEnabled = checkFeature(featureFlags.reservePriceExceptions);

  useEffect(() => {
    if (activeStep === ACTIVE_STEP.SUGGESTIONS) {
      setTableHeaders(['CURRENTLY', 'SUGGESTED', 'TOTAL']);
    } else if (activeStep === ACTIVE_STEP.OPPORTUNITIES) {
      setTableHeaders(['CURRENTLY', 'OPPORTUNITIES', 'TOTAL']);
    } else {
      setTableHeaders(['CURRENTLY']);
    }
  }, [offers, recalculation, activeStep]);

  const trucklaneOfferShifts = useMemo(() => {
    if (offerShiftsMappedToTrucklanes && trucklaneId && offerShiftsMappedToTrucklanes.has(trucklaneId)) {
      return offerShiftsMappedToTrucklanes.get(trucklaneId);
    }
    return [];
  }, [offerShiftsMappedToTrucklanes, trucklaneId]);

  const buyerMinimumsGap = useMemo(() => {
    if (!buyerMinimums || activeStep === ACTIVE_STEP.INACTIVE_LANES) return null;
    const { palletMinimum, weightMinimum } = applicableBuyerMinimums(buyerMinimums, user?.site?.trucklaneMinimums, offers[0]?.truckType);
    if (palletMinimum == null && weightMinimum == null) return null;
    const suggested = activeStep === ACTIVE_STEP.SUGGESTIONS || activeStep === ACTIVE_STEP.OPPORTUNITIES;
    if (suggested && recalculation) return null;
    const tableColumn = suggested ? 'TOTAL' : 'CURRENTLY';
    const pallets = accounting.unformat(
      calculatePallets(
        tableColumn,
        offers,
        recalculation,
        trucklaneOfferShifts,
        activeStep === ACTIVE_STEP.SUGGESTIONS,
        activeStep === ACTIVE_STEP.OPPORTUNITIES,
      ),
    );
    const weight = accounting.unformat(
      calculateWeight(
        tableColumn,
        offers,
        recalculation,
        trucklaneOfferShifts,
        activeStep === ACTIVE_STEP.SUGGESTIONS,
        activeStep === ACTIVE_STEP.OPPORTUNITIES,
      ),
    );
    const belowWeight = Boolean(weightMinimum) && weight < weightMinimum;
    const belowPallets = Boolean(palletMinimum) && pallets < palletMinimum;
    if (!belowPallets && !belowWeight) return null;
    return { tableColumn, belowPallets, belowWeight, palletGap: palletMinimum - pallets, weightGap: weightMinimum - weight };
  }, [buyerMinimums, user?.site?.trucklaneMinimums, offers, activeStep, recalculation, trucklaneOfferShifts]);

  const getMinimumsIcon = (tableRow, tableColumn) => {
    if (!buyerMinimumsGap || buyerMinimumsGap.tableColumn !== tableColumn) return null;
    if (tableRow === 'WEIGHT' && !buyerMinimumsGap.belowWeight) return null;
    if (tableRow === 'PALLETS' && !buyerMinimumsGap.belowPallets) return null;
    if (tableRow !== 'WEIGHT' && tableRow !== 'PALLETS') return null;
    return <AlertTriangleIcon className={classes.minimumsIcon} />;
  };

  const buildTableInfo = (tableRow, tableColumn) => {
    switch (tableRow) {
      case 'WEIGHT':
        return calculateWeight(
          tableColumn,
          offers,
          recalculation,
          trucklaneOfferShifts,
          activeStep === ACTIVE_STEP.SUGGESTIONS,
          activeStep === ACTIVE_STEP.OPPORTUNITIES,
        );
      case 'PALLETS':
        return calculatePallets(
          tableColumn,
          offers,
          recalculation,
          trucklaneOfferShifts,
          activeStep === ACTIVE_STEP.SUGGESTIONS,
          activeStep === ACTIVE_STEP.OPPORTUNITIES,
        );
      case 'LANE COST':
        return calculateLaneCost(
          user,
          tableColumn,
          offers,
          recalculation,
          trucklaneOfferShifts,
          activeStep === ACTIVE_STEP.SUGGESTIONS,
          activeStep === ACTIVE_STEP.OPPORTUNITIES,
        );
      case 'NET REVENUE':
        return calculateNetRevenue(
          user,
          tableColumn,
          offers,
          recalculation,
          trucklaneOfferShifts,
          activeStep === ACTIVE_STEP.SUGGESTIONS,
          activeStep === ACTIVE_STEP.OPPORTUNITIES,
        );
      case 'RECOVERY':
        return calculateRecovery(
          tableColumn,
          offers,
          recalculation,
          trucklaneOfferShifts,
          activeStep === ACTIVE_STEP.SUGGESTIONS,
          activeStep === ACTIVE_STEP.OPPORTUNITIES,
        );
      case 'TRUCK COUNT':
        return calculateTruckCount(
          user,
          tableColumn,
          offers,
          recalculation,
          trucklaneOfferShifts,
          activeStep === ACTIVE_STEP.SUGGESTIONS,
          activeStep === ACTIVE_STEP.OPPORTUNITIES,
        );
      default:
        return '—';
    }
  };

  const getSuggestionOrOpportunityColumn = (info) => {
    if (activeStep === ACTIVE_STEP.SUGGESTIONS || activeStep === ACTIVE_STEP.OPPORTUNITIES) {
      return (
        <span
          className={`${recalculation && activeStep === ACTIVE_STEP.SUGGESTIONS ? classes.sumaryTableEmptyInfo : classes.summaryTableInfo}`}
          style={reservePriceExceptionsEnabled ? { fontWeight: 'normal' } : {}}
        >
          {buildTableInfo(info, 'SUGGESTED')}
        </span>
      );
    }
  };

  const trucklaneSummaryInfo = useMemo(() => {
    return tableData.map((info) => (
      <div key={info} className={classes.summaryTableRow}>
        <span className={classes.summaryTableHeader}>{info}</span>
        <span
          className={`${classes.summaryTableInfo} ${reservePriceExceptionsEnabled && tableHeaders.length === 1 ? classes.alignRight : ''}`}
          style={reservePriceExceptionsEnabled ? { fontWeight: 'normal' } : {}}
        >
          {buildTableInfo(info, 'CURRENTLY')}
          {getMinimumsIcon(info, 'CURRENTLY')}
        </span>
        {getSuggestionOrOpportunityColumn(info)}
        {(activeStep === ACTIVE_STEP.SUGGESTIONS || activeStep === ACTIVE_STEP.OPPORTUNITIES) && (
          <span
            className={`${recalculation && activeStep === ACTIVE_STEP.SUGGESTIONS ? classes.sumaryTableEmptyInfo : classes.summaryTableInfo}`}
            style={reservePriceExceptionsEnabled ? { fontWeight: 'normal' } : {}}
          >
            {buildTableInfo(info, 'TOTAL')}
            {getMinimumsIcon(info, 'TOTAL')}
          </span>
        )}
      </div>
    ));
  }, [offers, activeStep, tableHeaders, reservePriceExceptionsEnabled, recalculation, buyerMinimumsGap]);

  const errorRefresh = () => {
    window.location.reload();
  };

  const suggestedOrShifts = trucklaneOfferShifts.length > 0 || activeStep === ACTIVE_STEP.SUGGESTIONS;

  const unawardOffers = Timing.throttle((e) => {
    e.stopPropagation();
    setActionInProgress(true);
    childAction(true);
    deSelect(trucklaneId, !(activeStep === ACTIVE_STEP.SUGGESTIONS));
    unawardOffersMutation({
      variables: {
        buyerSiteId: offers[0]?.buyerSiteId || '',
        truckType: offers[0]?.truckType || '',
        logisticsTerm: offers[0]?.logisticsTerm || '',
        transactionLocationId: offers[0]?.transactionLocationId || '',
        sellerLocationId: offers[0]?.sellerLocationId || '',
      },
      refetchQueries: [
        {
          query: AwardSummaryQuery,
        },
        {
          query: trucklanePollingQuery,
          variables: {
            pollingDetails: [...Store.trucklaneChannels.values()].map((channel) => ({ origin: channel.originDCId, truckType: channel.truckType })),
          },
        },
        {
          query: ActiveOfferShiftsQuery,
        },
      ],
    })
      .then((resp) => {
        childAction(false);
        if (resp.data?.unawardOfferListingsByTruckLane?.errors?.length) throw Error();
        AlertService.alert({ type: 'success', autoDismiss: true, message: <span>Offers have been unawarded</span> });
        Tracking.Event('Offers were unawarded in trucklane view', {
          userId: user._id,
        });
      })
      .catch(() => AlertService.alert({ type: 'warning', autoDismiss: true, message: <span>{OfferComparisonStrings.unawardingError}</span> }))
      .finally(() => {
        setActionInProgress(false);
      });
  }, 1000);

  const acceptTrucklaneOfferShifts = async () => {
    if (trucklaneOfferShifts.length > 0) {
      const acceptShiftResult = await acceptOfferShifts({
        variables: {
          offerShifts: formatShiftsForAcceptance(trucklaneOfferShifts),
        },
        refetchQueries: [
          {
            query: AwardSummaryQuery,
          },
          {
            query: ActiveOfferShiftsQuery,
          },
          {
            query: trucklanePollingQuery,
            variables: {
              pollingDetails: [...Store.trucklaneChannels.values()].map((channel) => ({ origin: channel.originDCId, truckType: channel.truckType })),
            },
          },
        ],
      });
      if (acceptShiftResult.data?.acceptShiftedOfferListings.errors && acceptShiftResult.data.acceptShiftedOfferListings.errors.length > 0) {
        throw new Error(acceptShiftResult.data.acceptShiftedOfferListings.errors[0].message);
      }
    }
  };

  const awardSuggestedOffers = async () => {
    const offerIds = offers
      .filter((offer) => offer.suggestions?.award?.suggested && offer.status === offerlistingStatuses.ACTIVE)
      .map((offer) => offer._id);
    if (offerIds.length === 0) return;
    childAction(true);
    const resp = await awardOffersMutation({
      variables: { offerListingIds: offerIds },
      refetchQueries: [
        {
          query: AwardSummaryQuery,
        },
        {
          query: trucklanePollingQuery,
          variables: {
            pollingDetails: [...Store.trucklaneChannels.values()].map((channel) => ({ origin: channel.originDCId, truckType: channel.truckType })),
          },
        },
      ],
    });
    childAction(false);
    if (resp.data?.awardSuggestedOfferListings?.errors?.length) {
      const recalcError = resp.data?.awardSuggestedOfferListings?.errors.some((error) => invalidErrorCodes.includes(error.code));
      if (recalcError) {
        Tracking.Event('There was a recalculation based error while awarding', {
          userId: user._id,
        });
        return AlertService.alert({
          type: 'warning',
          autoDismiss: false,
          message: (
            <div>
              <span>{OfferComparisonStrings.awardingError}</span>
              <div>
                <h1 className="error_action" onClick={errorRefresh}>
                  Refresh Suggested Awards
                </h1>
              </div>
            </div>
          ),
        });
      }
      throw Error();
    }
  };

  const awardSuggestions = Timing.throttle((e) => {
    e.stopPropagation();
    (async () => {
      try {
        setActionInProgress(true);
        await acceptTrucklaneOfferShifts();
        await awardSuggestedOffers();
        AlertService.alert({ type: 'success', autoDismiss: true, message: <span>Suggested Offers have been awarded</span> });
      } catch {
        AlertService.alert({ type: 'warning', autoDismiss: true, message: <span>{OfferComparisonStrings.awardingError}</span> });
      } finally {
        setActionInProgress(false);
      }
    })();
  }, 1000);

  const handleIgnoreOffers = (e) => {
    e.stopPropagation();
    ignoreOffers(e);
  };

  const handleRestoreOffers = (e) => {
    e.stopPropagation();
    restoreOffers(e);
  };

  const getIgnoreButton = () => {
    if (!reservePriceExceptionsEnabled) return null;
    const ignoredOffers = offers.filter((offer) => [offerlistingStatuses.IGNORED].includes(offer.status));

    const activeTruckLane = offers.filter((offer) => [offerlistingStatuses.AWARDED, offerlistingStatuses.ACCEPTED].includes(offer.status));
    const inRestoreMode = ignoredOffers.length > 0;
    const hasIgnorableOffers = offers.some((offer) => offer.status === offerlistingStatuses.ACTIVE);

    const allInActiveLane = activeTruckLane.length === offers.length;

    const disabled = ignoreOffersLoading || restoreOffersLoading || actionLoading || allInActiveLane || (!inRestoreMode && !hasIgnorableOffers);

    return (
      <MiniTooltip text={inRestoreMode ? 'Restore Offers' : 'Ignore Offers'}>
        <Button
          disabled={disabled}
          loadingText=""
          loading={ignoreOffersLoading || restoreOffersLoading}
          onClick={(e) => (inRestoreMode ? handleRestoreOffers(e) : handleIgnoreOffers(e))}
          icon={inRestoreMode ? icons.restore : icons.ignore}
          secondary
        />
      </MiniTooltip>
    );
  };

  const getUnawardButton = () => {
    if (reservePriceExceptionsEnabled && (activeStep === ACTIVE_STEP.SUGGESTIONS || activeStep === ACTIVE_STEP.OPPORTUNITIES)) {
      return (
        <MiniTooltip text="Unaward All">
          <Button
            disabled={!awardedOffers || awardOffersLoading || actionLoading}
            warning
            loadingText="Unawarding"
            loading={unawardOffersLoading}
            onClick={(e) => unawardOffers(e)}
            icon={UnawardIcon}
          />
        </MiniTooltip>
      );
    }
    return (
      <Button
        disabled={!awardedOffers || awardOffersLoading || actionLoading}
        warning
        loadingText="Unawarding"
        loading={unawardOffersLoading}
        onClick={(e) => unawardOffers(e)}
        icon={UnawardIcon}
      >
        Unaward All
      </Button>
    );
  };

  const awardOpportunities = Timing.throttle((e) => {
    e.stopPropagation();
    Tracking.Event('Opportunities were awarded in trucklane summary', {
      userId: user._id,
      transactionCycleId: offers[0]?.transactionCycleId || '',
    });
    (async () => {
      try {
        setActionInProgress(true);
        const offerIds = offers
          .filter((offer) => offer.suggestions?.reservePriceException?.suggested && offer.status === offerlistingStatuses.ACTIVE)
          .map((offer) => offer._id);
        if (offerIds.length === 0) return;
        childAction(true);
        const resp = await awardOpportunitiesMutation({
          variables: { offerListingIds: offerIds },
          refetchQueries: [
            {
              query: AwardSummaryQuery,
            },
            {
              query: trucklanePollingQuery,
              variables: {
                pollingDetails: [...Store.trucklaneChannels.values()].map((channel) => ({
                  origin: channel.originDCId,
                  truckType: channel.truckType,
                })),
              },
            },
            { query: trucklaneExceptionsQuery },
          ],
        });
        if (resp.data?.awardOpportunities?.errors?.length) throw Error(resp.data.awardOpportunities.errors[0].message);
        AlertService.alert({ type: 'success', autoDismiss: true, message: <span>Suggested Opportunities have been awarded</span> });
      } catch {
        AlertService.alert({ type: 'warning', autoDismiss: true, message: <span>{OfferComparisonStrings.awardingError}</span> });
      } finally {
        setActionInProgress(false);
        childAction(false);
      }
    })();
  }, 1000);

  const { activeTrucklane, hasBidsAboveReservePrice } = (() => {
    let activeTrucklane = false;
    let hasBidsAboveReservePrice = false;
    offers.forEach((offer) => {
      if (offer.status === offerlistingStatuses.AWARDED || offer.status === offerlistingStatuses.ACCEPTED) {
        activeTrucklane = true;
      }
      if (offer.suggestions?.reservePriceException?.suggested === true && offer.suggestions?.reservePriceException?.isException === false) {
        hasBidsAboveReservePrice = true;
      }
    });
    return { activeTrucklane, hasBidsAboveReservePrice };
  })();

  const displayBuyerMinimumsGap = () => {
    if (!buyerMinimumsGap || activeStep === ACTIVE_STEP.INACTIVE_LANES) return null;
    const { belowPallets, belowWeight, palletGap, weightGap } = buyerMinimumsGap;
    return (
      <div style={reservePriceExceptionsEnabled ? { marginTop: '-12px' } : {}} className={classes.minimumsAlert}>
        <AlertTriangleIcon />
        <span>{`${belowWeight ? `${accounting.formatNumber(weightGap)} lbs` : ''}${belowPallets && belowWeight ? ' & ' : ''}${
          belowPallets ? `${accounting.formatNumber(palletGap)} pallets` : ''
        } below the buyer pickup minimum`}</span>
      </div>
    );
  };

  return (
    <div
      className={`${suggestedOrShifts ? classes.suggestedSummary : classes.summary} ${reservePriceExceptionsEnabled ? classes.rightContainer : ''}`}
      data-testid="trucklane-card-summary"
    >
      {reservePriceExceptionsEnabled && activeStep === ACTIVE_STEP.OPPORTUNITIES && (
        <div className={classes.cardIqSummary}>
          <div className={classes.cardIqTitle}>
            <SparkleGradientIcon /> iQ SUMMARY
          </div>
          <div className={classes.cardIqDescription}>
            <span className={classes.cardIqHighlight}>{activeTrucklane ? 'Add to this lane' : 'Activate this lane'}</span>{' '}
            {hasBidsAboveReservePrice ? 'with bids above & below reserve price.' : 'with bids below reserve price.'}
          </div>
        </div>
      )}
      <div className={reservePriceExceptionsEnabled ? classes.summaryTableOverride : classes.summaryTable}>
        {reservePriceExceptionsEnabled ? null : <h1>TRUCK LANE SUMMARY</h1>}
        <div>
          <div className={classes.summaryTableRow}>
            <span className={classes.summaryTableCell} />
            {tableHeaders.map((header) =>
              header === 'SUGGESTED' || header === 'OPPORTUNITIES' ? (
                <span key={header} className={classes.summaryTableHeaderIq}>
                  <SparkleGradientIcon />
                  {header}
                </span>
              ) : (
                <span
                  key={header}
                  className={`${classes.summaryTableHeader} ${reservePriceExceptionsEnabled && tableHeaders.length === 1 ? classes.alignRight : ''}`}
                >
                  {header}
                </span>
              ),
            )}
          </div>
          {trucklaneSummaryInfo}
        </div>
        {recalculation && (
          <div className={classes.recalculationBanner}>
            <CalculatorIcon /> <h1>Recalculate to view updated suggestions</h1>
          </div>
        )}
        <div className={reservePriceExceptionsEnabled ? classes.summaryActionsOverride : classes.summaryActions}>
          {getIgnoreButton()}
          {getUnawardButton()}
          {suggestedOrShifts && (
            <Button
              disabled={recalculation || unawardOffersLoading || actionLoading}
              loadingText="Awarding Suggestions"
              loading={loadingAwards}
              onClick={(e) => awardSuggestions(e)}
              icon={SparkleIcon}
              className={classes.iq_icon}
              iq
            >
              Award Suggestions
            </Button>
          )}
          {reservePriceExceptionsEnabled && activeStep === ACTIVE_STEP.OPPORTUNITIES && (
            <Button
              disabled={recalculation || unawardOffersLoading || actionLoading}
              loadingText="Award Opportunities"
              loading={awardOpportunitiesLoading}
              onClick={(e) => awardOpportunities(e)}
              icon={SparkleIcon}
              className={classes.iq_icon}
              iq
            >
              Award Opportunities
            </Button>
          )}
        </div>
        {displayBuyerMinimumsGap()}
      </div>
    </div>
  );
};

TrucklaneCardSummary.propTypes = {
  offers: PropTypes.any,
  recalculation: PropTypes.bool,
  trucklaneId: PropTypes.string,
  awardSuggestion: PropTypes.func,
  selected: PropTypes.bool,
  deSelect: PropTypes.func,
  actionLoading: PropTypes.bool,
  childAction: PropTypes.func,
  user: PropTypes.object,
  activeStep: PropTypes.string,
  ignoreOffers: PropTypes.func,
  ignoreOffersLoading: PropTypes.bool,
  restoreOffers: PropTypes.func,
  restoreOffersLoading: PropTypes.bool,
  buyerMinimums: PropTypes.object,
};

export default TrucklaneCardSummary;
